import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  TextField,
  Typography
} from "@material-ui/core";
import React from "react";
import { useForm } from "react-hook-form";
import { isEmail } from "validator";
import EmailIcon from "../../../../../components/icons/email";
import SmsIcon from "../../../../../components/icons/sms";
import PhoneNumberInput, {
  validatePhoneNumber
} from "../../../../../components/phone-number-input";
import { prettifyErrorMessage } from "../../../../../helpers/error";
import useCustomSnackbar from "../../../../../hooks/use-custom-snackbar";
import useSubcountry from "../../../../../hooks/use-subcountry";
import useTranslate from "../../../../../hooks/use-translate";
import {
  isEmailAlreadyRegistered,
  isPhoneNumberAlreadyRegistered,
  sendUserAuthenticationEmailOtp,
  sendUserAuthenticationPhoneNumberOtp
} from "../../../../../services/user";
import CustomHeading from "./components/CustomHeading";

export type RegisterFormDataType = {
  givenName: string;
  familyName: string;
  phoneNumber?: string;
  email: string;
  tos: boolean;
};

type Props = {
  onSubmit: (data: RegisterFormDataType) => void;
  onSwitchToSignIn?: () => void;
  labels?: {
    title?: string | false;
    subtitle?: string;
  };
};

export default function RegisterForm({
  onSubmit,
  labels,
  onSwitchToSignIn
}: Props) {
  const t = useTranslate();
  const { subcountry } = useSubcountry();
  const snackbar = useCustomSnackbar();

  const { handleSubmit, register, errors, formState } =
    useForm<RegisterFormDataType>({
      mode: "onBlur"
    });

  const handleFormSubmit = async (data: RegisterFormDataType) => {
    try {
      await sendUserAuthenticationEmailOtp(data.email);
      onSubmit(data);
    } catch (e) {
      snackbar.showError(prettifyErrorMessage(e?.message));
    }
  };

  return (
    <>
      <Box mb={{ xs: 4, md: 6 }}>
        <CustomHeading
          dangerouslySetInnerHTML={{
            __html:
              labels?.title ||
              t(
                subcountry === "CA"
                  ? "authentication.signup.title.california"
                  : "authentication.signup.title"
              )
          }}
        />

        {subcountry === "CA" && (
          <Box mt={2}>
            <Typography
              variant="body1"
              dangerouslySetInnerHTML={{
                __html: t("authentication.signup.description.california")
              }}
            />
          </Box>
        )}
      </Box>

      {/* First & Last name */}
      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        style={{
          width: "100%"
        }}
      >
        <Box display="flex" flexDirection="column" alignItems="stretch">
          {/* First & last name */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={2}
          >
            <Box flex={1} mr={1}>
              <TextField
                variant="outlined"
                id="givenName"
                name="givenName"
                label={`${t("authentication.firstName")}*`}
                placeholder={`${t("authentication.firstName")}*`}
                fullWidth
                inputRef={register({
                  required: t("error.required"),
                  maxLength: "255"
                })}
                error={!!errors.givenName}
                helperText={errors.givenName?.message}
                disabled={formState.isSubmitting}
                autoComplete="given-name"
              />
            </Box>
            <Box flex={1} ml={1}>
              <TextField
                variant="outlined"
                id="familyName"
                name="familyName"
                label={`${t("authentication.lastName")}*`}
                placeholder={`${t("authentication.lastName")}*`}
                fullWidth
                inputRef={register({
                  required: t("error.required"),
                  maxLength: "255"
                })}
                error={!!errors.familyName}
                helperText={errors.familyName?.message}
                disabled={formState.isSubmitting}
                autoComplete="family-name"
              />
            </Box>
          </Box>

          {/* Phone number */}
          {/* <Box mb={2}>
            <TextField
              id="phoneNumber"
              name="phoneNumber"
              variant="outlined"
              label={`${t("phoneNumber")}*`}
              placeholder="(000) 000-0000"
              fullWidth
              inputRef={register({
                required: t("error.required"),
                validate: {
                  validFormat: validatePhoneNumber,
                  notAlreadyRegistered: async value => {
                    const phoneAlreadyRegistered =
                      await isPhoneNumberAlreadyRegistered(value);
                    if (phoneAlreadyRegistered) {
                      return t("authentication.signup.phoneAlreadyExists");
                    }
                    return true;
                  }
                }
              })}
              error={!!errors.phoneNumber}
              helperText={
                errors.phoneNumber?.message ||
                t("authentication.youWillReceiveSms")
              }
              disabled={formState.isSubmitting}
              autoComplete="tel"
              InputProps={{
                inputComponent: PhoneNumberInput,
                startAdornment: <SmsIcon />
              }}
            />
          </Box> */}

          {/* Email */}
          <Box mb={2}>
            <TextField
              variant="outlined"
              id="email"
              name="email"
              autoComplete="email"
              fullWidth
              label={`${t("email")}*`}
              placeholder={t("authentication.emailPlaceholder")}
              inputRef={register({
                required: t("error.required"),
                validate: {
                  validFormat: (value: string) => {
                    if (!isEmail(value)) {
                      return t("error.invalidEmail");
                    }
                    return true;
                  },
                  notAlreadyRegistered: async value => {
                    const emailAlreadyRegistered =
                      await isEmailAlreadyRegistered(value);
                    if (emailAlreadyRegistered) {
                      return t("authentication.signup.emailAlreadyExists");
                    }
                    return true;
                  }
                }
              })}
              error={!!errors.email}
              disabled={formState.isSubmitting}
              helperText={errors.email?.message}
              InputProps={{
                startAdornment: <EmailIcon />
              }}
            />
          </Box>

          {/* TOS Checkbox */}
          <Box mb={3}>
            <FormControlLabel
              control={
                <Checkbox
                  inputRef={register({
                    required: t("authentication.tos.required")
                  })}
                  name="tos"
                />
              }
              label={
                <Typography
                  variant="body2"
                  dangerouslySetInnerHTML={{
                    __html: t("authentication.tos")
                  }}
                  component="span"
                  color={errors.tos ? "error" : "textPrimary"}
                />
              }
            />
            {errors.tos && (
              <FormHelperText error>{errors.tos.message}</FormHelperText>
            )}
          </Box>

          <Box>
            <Button fullWidth variant="contained" color="primary" type="submit">
              {t("go")}
            </Button>
          </Box>
        </Box>
      </form>

      {/* Sign in instead */}
      {onSwitchToSignIn && (
        <Box mt={4} display="flex" justifyContent="center">
          <Button
            variant="text"
            color="secondary"
            size="small"
            onClick={onSwitchToSignIn}
          >
            {t("authentication.signInInstead")}
          </Button>
        </Box>
      )}
    </>
  );
}
